import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatPhoneOnBlur } from '@/lib/phone';
import { getStates } from '../api/leads-api';
import type { LeadFormData } from '../types';

interface AddressSectionProps {
  formData: LeadFormData;
  onChange: (field: keyof LeadFormData, value: string | number | boolean | undefined) => void;
}

function PhoneRow({
  id,
  label,
  value,
  labelId,
  labelValue,
  onChange,
  onLabelChange,
  phoneTabIndex,
  labelTabIndex = -1,
}: {
  id: string;
  label: string;
  value: string;
  labelId: string;
  labelValue: string;
  onChange: (value: string) => void;
  onLabelChange: (value: string) => void;
  /** Tab index for the phone-number input. Omit/undefined to keep default (in tab flow). */
  phoneTabIndex?: number;
  /** Tab index for the inline label input. Defaults to -1 — labels are out of the spec'd tab flow. */
  labelTabIndex?: number;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-[10px]">{label}</Label>
      <div className="flex gap-1">
        <Input
          id={id}
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onChange(formatPhoneOnBlur(e.target.value))}
          tabIndex={phoneTabIndex}
          className="h-6 text-xs flex-[3] text-left"
        />
        <Input
          id={labelId}
          type="text"
          value={labelValue}
          onChange={(e) => onLabelChange(e.target.value)}
          maxLength={20}
          tabIndex={labelTabIndex}
          className="h-6 text-xs flex-[2] text-left"
        />
      </div>
    </div>
  );
}

export function AddressSection({ formData, onChange }: AddressSectionProps) {
  const states = getStates();
  const isSame = formData.hasSameAddress;

  return (
    <div className="grid grid-cols-4 gap-2">
      {/* Property Address */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Property Address</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="address1" className="text-[10px]">Address 1 *</Label>
            <Input
              id="address1"
              value={formData.address1}
              onChange={(e) => onChange('address1', e.target.value)}
              required
              className="h-6 text-xs text-left"
            />
          </div>
          <div>
            <Label htmlFor="address2" className="text-[10px]">Address 2</Label>
            <Input
              id="address2"
              value={formData.address2 || ''}
              onChange={(e) => onChange('address2', e.target.value)}
              tabIndex={-1}
              className="h-6 text-xs text-left"
            />
          </div>
          <div>
            <Label htmlFor="city" className="text-[10px]">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => onChange('city', e.target.value)}
              required
              className="h-6 text-xs text-left"
            />
          </div>
          {/* State and Zip are sized to their content rather than stretched to
              fill the column. A two-character state code in a half-column-wide
              box is most of why this form reads as inflated next to the legacy
              screen. Cell assignment and row count are unchanged — the boxes
              shrink where they already sit. */}
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label htmlFor="state" className="text-[10px]">State *</Label>
              <Input
                id="state"
                value="OH"
                disabled
                tabIndex={-1}
                className="bg-gray-50 h-6 w-12 text-xs text-left"
              />
            </div>
            <div>
              <Label htmlFor="zip" className="text-[10px]">Zip *</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => onChange('zip', e.target.value)}
                maxLength={10}
                required
                className="h-6 w-24 text-xs text-left"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address - always visible, disabled when same as property */}
      {/* Per AT-20 Phase 4: billing fields are NOT in the spec'd tab flow. */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Billing Address</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="billingAddress1" className="text-[10px]">Address 1</Label>
            <Input
              id="billingAddress1"
              value={isSame ? formData.address1 : (formData.billingAddress1 || '')}
              onChange={(e) => onChange('billingAddress1', e.target.value)}
              disabled={isSame}
              tabIndex={-1}
              className={cn("h-6 text-xs text-left", isSame && "bg-gray-50")}
            />
          </div>
          <div>
            <Label htmlFor="billingAddress2" className="text-[10px]">Address 2</Label>
            <Input
              id="billingAddress2"
              value={isSame ? (formData.address2 || '') : (formData.billingAddress2 || '')}
              onChange={(e) => onChange('billingAddress2', e.target.value)}
              disabled={isSame}
              tabIndex={-1}
              className={cn("h-6 text-xs text-left", isSame && "bg-gray-50")}
            />
          </div>
          <div>
            <Label htmlFor="billingCity" className="text-[10px]">City</Label>
            <Input
              id="billingCity"
              value={isSame ? formData.city : (formData.billingCity || '')}
              onChange={(e) => onChange('billingCity', e.target.value)}
              disabled={isSame}
              tabIndex={-1}
              className={cn("h-6 text-xs text-left", isSame && "bg-gray-50")}
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label htmlFor="billingState" className="text-[10px]">State</Label>
              <Select
                id="billingState"
                value={isSame ? formData.state : (formData.billingState || '')}
                onChange={(e) => onChange('billingState', e.target.value)}
                disabled={isSame}
                tabIndex={-1}
                className={cn("h-6 w-20 text-xs text-left", isSame && "bg-gray-50")}
              >
                <option value="">-</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.code}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="billingZip" className="text-[10px]">Zip</Label>
              <Input
                id="billingZip"
                value={isSame ? formData.zip : (formData.billingZip || '')}
                onChange={(e) => onChange('billingZip', e.target.value)}
                disabled={isSame}
                maxLength={10}
                tabIndex={-1}
                className={cn("h-6 w-24 text-xs text-left", isSame && "bg-gray-50")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Contact Information</h4>
        <div className="space-y-0.5">
          <PhoneRow
            id="cellPhone"
            label="Cell Phone"
            value={formData.cellPhone || ''}
            labelId="cellPhoneLabel"
            labelValue={formData.cellPhoneLabel || ''}
            onChange={(v) => onChange('cellPhone', v)}
            onLabelChange={(v) => onChange('cellPhoneLabel', v)}
          />
          <PhoneRow
            id="homePhone"
            label="Home Phone"
            value={formData.homePhone || ''}
            labelId="homePhoneLabel"
            labelValue={formData.homePhoneLabel || ''}
            onChange={(v) => onChange('homePhone', v)}
            onLabelChange={(v) => onChange('homePhoneLabel', v)}
          />
          <PhoneRow
            id="workPhone"
            label="Work Phone"
            value={formData.workPhone || ''}
            labelId="workPhoneLabel"
            labelValue={formData.workPhoneLabel || ''}
            onChange={(v) => onChange('workPhone', v)}
            onLabelChange={(v) => onChange('workPhoneLabel', v)}
          />
          <PhoneRow
            id="otherPhone"
            label="Other Phone"
            value={formData.otherPhone || ''}
            labelId="otherPhoneLabel"
            labelValue={formData.otherPhoneLabel || ''}
            onChange={(v) => onChange('otherPhone', v)}
            onLabelChange={(v) => onChange('otherPhoneLabel', v)}
            phoneTabIndex={-1}
          />
          <div>
            <Label htmlFor="email" className="text-[10px]">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
              tabIndex={-1}
              className="h-6 text-xs text-left"
            />
          </div>
        </div>
      </div>

      {/* Tenant Information */}
      {/* Per AT-20 Phase 4: tenant fields are NOT in the spec'd tab flow. */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Tenant Information</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="tenantName" className="text-[10px]">Tenant Name</Label>
            <Input
              id="tenantName"
              value={formData.tenantName || ''}
              onChange={(e) => onChange('tenantName', e.target.value)}
              tabIndex={-1}
              className="h-6 text-xs text-left"
            />
          </div>
          <PhoneRow
            id="tenantCellPhone"
            label="Cell Phone"
            value={formData.tenantCellPhone || ''}
            labelId="tenantCellPhoneLabel"
            labelValue={formData.tenantCellPhoneLabel || ''}
            onChange={(v) => onChange('tenantCellPhone', v)}
            onLabelChange={(v) => onChange('tenantCellPhoneLabel', v)}
            phoneTabIndex={-1}
          />
          <PhoneRow
            id="tenantHomePhone"
            label="Home Phone"
            value={formData.tenantHomePhone || ''}
            labelId="tenantHomePhoneLabel"
            labelValue={formData.tenantHomePhoneLabel || ''}
            onChange={(v) => onChange('tenantHomePhone', v)}
            onLabelChange={(v) => onChange('tenantHomePhoneLabel', v)}
            phoneTabIndex={-1}
          />
          <PhoneRow
            id="tenantWorkPhone"
            label="Work Phone"
            value={formData.tenantWorkPhone || ''}
            labelId="tenantWorkPhoneLabel"
            labelValue={formData.tenantWorkPhoneLabel || ''}
            onChange={(v) => onChange('tenantWorkPhone', v)}
            onLabelChange={(v) => onChange('tenantWorkPhoneLabel', v)}
            phoneTabIndex={-1}
          />
          <PhoneRow
            id="tenantOtherPhone"
            label="Other Phone"
            value={formData.tenantOtherPhone || ''}
            labelId="tenantOtherPhoneLabel"
            labelValue={formData.tenantOtherPhoneLabel || ''}
            onChange={(v) => onChange('tenantOtherPhone', v)}
            onLabelChange={(v) => onChange('tenantOtherPhoneLabel', v)}
            phoneTabIndex={-1}
          />
        </div>
      </div>
    </div>
  );
}
